import { Chip, Typography } from '@mui/material';
import { EXCLUDED_VALUE, UNMAPPED } from 'business/constants';
import { formatDisplay } from 'helpers/formatHelper';
import { translateBlockValue } from 'i18n';
import type { FieldSynopsis } from 'modules/datacatalog/types';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Flex from 'ui/Flex/Flex';

import FieldDistributionChart from './FieldDistributionChart';
import {
    isCategoryLikeType,
    isDateType,
    isEnumType,
    isNumericType,
} from './typeHelpers';

type Props = {
    data: Extract<FieldSynopsis, { hasSynopsis: true }>;
};

type LabeledStatProps = { label: string; value: ReactNode };
const LabeledStat = ({ label, value }: LabeledStatProps) => (
    <Flex direction="row" gap={1} alignItems="baseline">
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="body2">{value}</Typography>
    </Flex>
);

type StatSectionProps = { label: string; children: ReactNode };
const StatSection = ({ label, children }: StatSectionProps) => (
    <Flex direction="column" gap={0.5}>
        <Typography variant="subtitle2">{label}</Typography>
        {children}
    </Flex>
);

type StatItemProps = { label: string; value: ReactNode };
const StatItem = ({ label, value }: StatItemProps) => (
    <Flex direction="column" gap={0.5}>
        <Typography variant="subtitle2">{label}</Typography>
        <Typography variant="body2">{value}</Typography>
    </Flex>
);

const FieldDataPreview = ({ data }: Props) => {
    const { t } = useTranslation();

    const completion =
        data.ratioEmptyRows !== undefined ? 1 - data.ratioEmptyRows : null;

    const formatExampleValue = (value: unknown) => {
        if (isEnumType(data.type)) {
            return translateBlockValue(String(value).toUpperCase());
        }

        if (value === UNMAPPED) {
            return t(UNMAPPED);
        }

        return String(value);
    };

    return (
        <Flex direction="column" gap={1}>
            {completion !== null && (
                <LabeledStat
                    label={t('dataCatalog.completion')}
                    value={`${Math.round(completion * 100)}%`}
                />
            )}

            {data.examples && data.examples.length > 0 && (
                <LabeledStat
                    label={t('dataCatalog.examples')}
                    value={data.examples
                        .filter((value) => value !== EXCLUDED_VALUE)
                        .map(formatExampleValue)
                        .join(', ')}
                />
            )}

            <StatSection label={t('dataCatalog.usableAs')}>
                <Flex direction="row" gap={1} flexWrap="wrap">
                    {data.isMetric && (
                        <Chip
                            label={t('dataCatalog.metric')}
                            color="primary"
                            variant="filled"
                            size="small"
                        />
                    )}
                    {data.isDimension && (
                        <Chip
                            label={t('dataCatalog.dimension')}
                            color="primary"
                            variant="filled"
                            size="small"
                        />
                    )}
                    {data.isFilter && (
                        <Chip
                            label={t('dataCatalog.filter')}
                            color="primary"
                            variant="filled"
                            size="small"
                        />
                    )}
                </Flex>
            </StatSection>

            {(isNumericType(data.type) || isDateType(data.type)) && (
                <Flex direction="row" gap={2} flexWrap="wrap">
                    {data.minimum !== undefined && data.minimum !== null && (
                        <StatItem
                            label={t('dataCatalog.minimum')}
                            value={formatDisplay(data.minimum, data.type)}
                        />
                    )}
                    {data.maximum !== undefined && data.maximum !== null && (
                        <StatItem
                            label={t('dataCatalog.maximum')}
                            value={formatDisplay(data.maximum, data.type)}
                        />
                    )}
                    {isNumericType(data.type) &&
                        data.average !== undefined &&
                        data.average !== null && (
                            <StatItem
                                label={t('dataCatalog.average')}
                                value={formatDisplay(data.average, data.type)}
                            />
                        )}
                </Flex>
            )}

            {isCategoryLikeType(data.type) &&
                data.distinctCount !== undefined &&
                data.distinctCount !== null && (
                    <LabeledStat
                        label={t('dataCatalog.distinctCount')}
                        value={data.distinctCount}
                    />
                )}

            {isEnumType(data.type) &&
                data.distribution &&
                data.distribution.length > 0 && (
                    <StatSection label={t('dataCatalog.distribution')}>
                        <FieldDistributionChart buckets={data.distribution} />
                    </StatSection>
                )}
        </Flex>
    );
};

export default FieldDataPreview;
